import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import AdminLayout from "../../layouts/AdminLayout";
import NotificationModal from "./components/NotificationModal";
import AddYachtModal from "./components/AddYachtModal";

function YachtManagement() {
    const [yachts, setYachts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showAddForm, setShowAddForm] = useState(false);
    const [newYacht, setNewYacht] = useState({
        name: "",
        captain: "",
        type: "",
        capacity: "",
        crew: "",
        model: "",
        year: "",
        length: "",
        price: "", // Günlük kiralama fiyatı
        status: "Aktif",
        images: [], // Base64 format resimler için
    });
    const [imageDescriptions, setImageDescriptions] = useState({});

    const [notification, setNotification] = useState({
        show: false,
        type: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [deleteConfirm, setDeleteConfirm] = useState({
        show: false,
        yacht: null,
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState({
        show: false,
        type: 'success',
        title: '',
        message: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredYachts, setFilteredYachts] = useState([]);

    const handleCloseModal = () => {
        // Preview URL'lerini temizle
        newYacht.images.forEach((img) => {
            if (img.preview) {
                URL.revokeObjectURL(img.preview);
            }
        });
        setImageDescriptions({});
        setShowAddForm(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewYacht({
            ...newYacht,
            [name]: value,
        });

        // Hata mesajını temizle
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: "",
            });
        }
    };

    // Resim dosyasını base64'e çeviren fonksiyon
    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    // Dosya validasyonu
    const validateFile = (file) => {
        const validTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
        ];
        const maxSize = 5 * 1024 * 1024; // 5MB

        if (!validTypes.includes(file.type)) {
            setNotification({
                show: true,
                type: "error",
                message:
                    "Sadece JPG, PNG ve WebP formatları desteklenmektedir.",
            });
            return false;
        }

        if (file.size > maxSize) {
            setNotification({
                show: true,
                type: "error",
                message: "Dosya boyutu 5MB'dan büyük olamaz.",
            });
            return false;
        }

        return true;
    };

    // Resim yükleme fonksiyonu
    const handleImageUpload = async (files) => {
        if (newYacht.images.length + files.length > 10) {
            setNotification({
                show: true,
                type: "error",
                message: "En fazla 10 resim yükleyebilirsiniz.",
            });
            return;
        }

        setImageUploading(true);
        const newImages = [];

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (validateFile(file)) {
                    const base64 = await convertToBase64(file);
                    const imageId = Date.now() + i;
                    newImages.push({
                        id: imageId,
                        name: file.name,
                        size: file.size,
                        base64: base64,
                        preview: URL.createObjectURL(file),
                    });
                    
                    // Initialize description for new image
                    setImageDescriptions(prev => ({
                        ...prev,
                        [imageId]: ''
                    }));
                }
            }

            setNewYacht({
                ...newYacht,
                images: [...newYacht.images, ...newImages],
            });

            if (newImages.length > 0) {
                setNotification({
                    show: true,
                    type: "success",
                    message: `${newImages.length} resim başarıyla yüklendi.`,
                });
            }
        } catch (error) {
            setNotification({
                show: true,
                type: "error",
                message: "Resim yüklenirken bir hata oluştu.",
            });
        } finally {
            setImageUploading(false);
        }
    };

    // Dosya input change handler
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            handleImageUpload(files);
        }
        // Input'u temizle
        e.target.value = "";
    };

    // Drag & Drop handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = Array.from(e.dataTransfer.files);
        if (files && files.length > 0) {
            handleImageUpload(files);
        }
    };

    // Resim silme fonksiyonu
    const removeImage = (imageId) => {
        const imageToRemove = newYacht.images.find((img) => img.id === imageId);
        if (imageToRemove && imageToRemove.preview) {
            URL.revokeObjectURL(imageToRemove.preview);
        }

        setNewYacht({
            ...newYacht,
            images: newYacht.images.filter((img) => img.id !== imageId),
        });
        
        // Remove description for this image
        setImageDescriptions(prev => {
            const newDescriptions = { ...prev };
            delete newDescriptions[imageId];
            return newDescriptions;
        });
    };
    
    // Handle image description change
    const handleImageDescriptionChange = (imageId, description) => {
        setImageDescriptions(prev => ({
            ...prev,
            [imageId]: description
        }));
    };

    // Form validasyonu
    const validateForm = () => {
        const errors = {};

        if (!newYacht.name.trim()) {
            errors.name = "Yat adı gereklidir";
        }

        if (!newYacht.captain.trim()) {
            errors.captain = "Kaptan adı gereklidir";
        }

        if (!newYacht.type) {
            errors.type = "Yat tipi seçilmelidir";
        }

        if (!newYacht.capacity || newYacht.capacity <= 0) {
            errors.capacity = "Geçerli bir kapasite girilmelidir";
        }

        if (!newYacht.model.trim()) {
            errors.model = "Model adı gereklidir";
        }

        if (
            !newYacht.year ||
            newYacht.year < 1900 ||
            newYacht.year > new Date().getFullYear()
        ) {
            errors.year = "Geçerli bir üretim yılı girilmelidir";
        }

        if (!newYacht.length || newYacht.length <= 0) {
            errors.length = "Geçerli bir uzunluk girilmelidir";
        }

        if (!newYacht.price || newYacht.price <= 0) {
            errors.price = "Geçerli bir günlük kiralama fiyatı girilmelidir";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Form validasyonu
        if (!validateForm()) {
            setNotification({
                show: true,
                type: "error",
                message: "Lütfen tüm gerekli alanları doldurun",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const token = localStorage.getItem("authToken");
            if (!token) {
                throw new Error("Token bulunamadı. Lütfen tekrar giriş yapın.");
            }

            // 1. Aşama: Yat bilgilerini ekle
            const yachtData = {
                name: newYacht.name.trim(),
                captain: newYacht.captain.trim(),
                type: parseInt(newYacht.type),
                capacity: parseInt(newYacht.capacity),
                model: newYacht.model.trim(),
                year: parseInt(newYacht.year),
                shipBoy: newYacht.crew || "Belirtilmemiş",
                length: parseInt(newYacht.length),
                grossTone: 0,
                engineInformation: "Belirtilmemiş",
                flag: "Turkey",
                phone: "+90",
                location: "Belirtilmemiş",
                price: parseFloat(newYacht.price),
                currency: "TRY",
                description: "Belirtilmemiş",
                features: "Belirtilmemiş",
                availabilities: []
            };

            const yachtResponse = await fetch("/rest/Yacht/addYacht", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(yachtData),
            });

            if (!yachtResponse.ok) {
                const errorText = await yachtResponse.text();
                throw new Error(
                    `Yat bilgileri eklenemedi. Hata: ${yachtResponse.status} - ${errorText}`
                );
            }

            const yachtResult = await yachtResponse.json();
            console.log("Yat ekleme response:", yachtResult);

            if (!yachtResult.yachtId) {
                throw new Error("Yat ID alınamadı. Lütfen tekrar deneyin.");
            }

            const yachtId = yachtResult.yachtId;

            // 2. Aşama: Resimleri ekle (eğer varsa)
            let allImagesSuccessful = true;
            let failedImageCount = 0;
            const hasImages = newYacht.images && newYacht.images.length > 0;

            if (hasImages) {
                console.log(`${newYacht.images.length} resim ekleniyor...`);
                
                for (let i = 0; i < newYacht.images.length; i++) {
                    const image = newYacht.images[i];
                    
                    const imageData = {
                        yachtId: yachtId,
                        imageAsBytes: image.base64.split(',')[1], // data:image/jpeg;base64, kısmını çıkar
                        description: imageDescriptions[image.id] || null,
                        order: i + 1
                    };

                    console.log(`Resim ${i + 1} ekleniyor, yachtId: ${yachtId}, order: ${i + 1}`);
                    console.log(`Resim ${i + 1} base64 uzunluğu:`, imageData.imageAsBytes.length);

                    console.log("Resim ekleme API'si çağrılıyor:", "/rest/Yacht/addImageToYacht");
                    const imageResponse = await fetch("/rest/Yacht/addImageToYacht", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(imageData),
                    });

                    console.log(`Resim ${i + 1} response status:`, imageResponse.status);

                    if (!imageResponse.ok) {
                        const errorText = await imageResponse.text();
                        console.error(`Resim ${i + 1} eklenemedi - status: ${imageResponse.status}, error:`, errorText);
                        allImagesSuccessful = false;
                        failedImageCount++;
                    } else {
                        const imageResult = await imageResponse.json();
                        console.log(`Resim ${i + 1} başarıyla eklendi:`, imageResult);
                    }
                }
            }

            // Sonuç değerlendirmesi
            if (!hasImages || allImagesSuccessful) {
                // Resim yoksa veya tüm resimler başarılı ise modal göster
                showModal('success', 'Başarılı!', 'Yat başarıyla eklendi');
            } else {
                // Resim ekleme hatası varsa bilgilendir
                showModal('warning', 'Uyarı!', `Yat eklendi ancak ${failedImageCount} resim eklenemedi.`);
            }

            // Form'u temizle ve kapat
            // Önce mevcut preview URL'lerini temizle
            newYacht.images.forEach((img) => {
                if (img.preview) {
                    URL.revokeObjectURL(img.preview);
                }
            });

            setNewYacht({
                name: "",
                captain: "",
                type: "",
                capacity: "",
                crew: "",
                model: "",
                year: "",
                length: "",
                price: "",
                status: "Aktif",
                images: [],
            });
            setImageDescriptions({});
            setFormErrors({});
            setShowAddForm(false);

            // Yat listesini güncelle
            fetchYachts();

        } catch (error) {
            console.error("Yat ekleme hatası detayı:", error);
            // Hata bildirimi göster
            setNotification({
                show: true,
                type: "error",
                message: `Yat eklenirken bir hata oluştu: ${error.message}`,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Kullanıcıya ait yat listesini API'den çekmek için fetch fonksiyonu
    const fetchYachts = async () => {
        try {
            setLoading(true);

            // localStorage'dan token'ı al
            const token = localStorage.getItem("authToken");

            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            const response = await fetch("/rest/yacht/getYachtListByUserId", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`, // Token'ı header'a ekle
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error("Token geçersiz veya süresi dolmuş");
                }
                throw new Error("Yatlar getirilemedi");
            }

            const data = await response.json();
            console.log("API'den gelen kullanıcı yat verileri:", data);

            // API'den gelen veriyi kontrol et - direkt array döndürüyor
            if (Array.isArray(data)) {
                setYachts(data);
                console.log(`${data.length} adet kullanıcı yatı bulundu`);
            } else if (data.value && Array.isArray(data.value)) {
                setYachts(data.value);
                console.log(`${data.value.length} adet kullanıcı yatı bulundu (value property'den)`);
            } else {
                console.warn("Beklenmeyen veri formatı:", data);
                setYachts([]);
            }

            setError(null);
        } catch (error) {
            console.error("Yatları getirme hatası:", error);
            setError(error.message || "Yatlar yüklenirken bir hata oluştu");
        } finally {
            setLoading(false);
        }
    };

    // Yat silme fonksiyonu
    const deleteYacht = async (yachtId) => {
        try {
            setIsDeleting(true);

            const token = localStorage.getItem("authToken");
            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            const response = await fetch(`/rest/Yacht/deleteYacht/${yachtId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error("Token geçersiz veya süresi dolmuş");
                }
                if (response.status === 404) {
                    throw new Error("Yat bulunamadı");
                }
                throw new Error("Yat silinemedi");
            }

            // Başarılı bildirim göster
            showModal('success', 'Başarılı!', 'Yat başarıyla silindi!');

            // Yat listesini güncelle
            fetchYachts();

            // Confirmation dialog'u kapat
            setDeleteConfirm({ show: false, yacht: null });
        } catch (error) {
            console.error("Yat silme hatası:", error);
            showModal('error', 'Hata!', `Yat silinirken bir hata oluştu: ${error.message}`);
        } finally {
            setIsDeleting(false);
        }
    };

    // Silme confirmation'ı göster
    const showDeleteConfirmation = (yacht) => {
        setDeleteConfirm({ show: true, yacht });
    };

    // Silme confirmation'ı kapat
    const hideDeleteConfirmation = () => {
        setDeleteConfirm({ show: false, yacht: null });
    };

    // Notification modalını göster
    const showModal = (type = 'success', title, message) => {
        setShowNotificationModal({
            show: true,
            type,
            title,
            message
        });
    };

    // Notification modalını kapat
    const hideNotificationModal = () => {
        setShowNotificationModal({
            show: false,
            type: 'success',
            title: '',
            message: ''
        });
    };

    // Component mount olduğunda yatları getir
    useEffect(() => {
        fetchYachts();
    }, []);

    // Yatları filtreleme
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredYachts(yachts);
        } else {
            const filtered = yachts.filter(yacht => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    yacht.name?.toLowerCase().includes(searchLower) ||
                    yacht.captain?.toLowerCase().includes(searchLower) ||
                    yacht.model?.toLowerCase().includes(searchLower) ||
                    getYachtTypeText(yacht.type).toLowerCase().includes(searchLower)
                );
            });
            setFilteredYachts(filtered);
        }
    }, [yachts, searchTerm]);

    // Notification auto-hide
    useEffect(() => {
        if (notification.show) {
            const timer = setTimeout(() => {
                setNotification({ show: false, type: "", message: "" });
            }, 5000); // 5 saniye sonra kaybolur

            return () => clearTimeout(timer);
        }
    }, [notification.show]);

    // Modal açıkken body scroll'unu kapat
    useEffect(() => {
        if (showAddForm || deleteConfirm.show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showAddForm, deleteConfirm.show]);

    // ESC tuşu ile modalları kapat
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (showAddForm) {
                    handleCloseModal();
                }
                if (deleteConfirm.show) {
                    hideDeleteConfirmation();
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showAddForm, deleteConfirm.show]);

    // Yat tipini sayıdan metne çeviren fonksiyon
    const getYachtTypeText = (typeNumber) => {
        const types = {
            1: "Motor Yacht",
            2: "Sailing Yacht",
            3: "Catamaran",
            4: "Luxury Yacht",
            5: "Super Yacht",
        };
        return types[typeNumber] || "Bilinmiyor";
    };

    // Search input change handler
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Search temizleme
    const clearSearch = () => {
        setSearchTerm('');
    };

    return (
        <AdminLayout currentPage="yachts">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h1 className="text-2xl font-bold">Yat Yönetimi</h1>
                            <p className="text-white/60">
                                {searchTerm ? `${filteredYachts.length} yat bulundu (${yachts.length} toplam)` : `Toplam ${yachts.length} yat`}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Tekne ara..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className="px-4 py-2 pl-10 pr-10 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 transition-all duration-300"
                                />
                                <svg
                                    className="w-5 h-5 text-white/30 absolute left-3 top-2.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                                {searchTerm && (
                                    <button
                                        onClick={clearSearch}
                                        className="absolute right-3 top-2.5 text-white/30 hover:text-white/60 transition-colors"
                                        title="Aramayı temizle"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                Yeni Yat Ekle
                            </button>
                        </div>
                    </div>

                    {/* Loading Durumu */}
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    )}

                    {/* Hata Durumu */}
                    {error && (
                        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-8">
                            <p className="text-red-400">{error}</p>
                        </div>
                    )}

                    {/* Yat Listesi */}
                    {!loading && !error && (
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="text-white/60 border-b border-white/10">
                                            <th className="text-left pb-3 font-medium">
                                                Yat Adı
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Kaptan
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Tip
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Kapasite
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Mürettebat
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Model/Yıl
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Uzunluk
                                            </th>
                                            <th className="text-left pb-3 font-medium">
                                                Durum
                                            </th>
                                            <th className="text-center pb-3 font-medium">
                                                İşlemler
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredYachts.map((yacht) => (
                                            <tr
                                                key={yacht.id}
                                                className="border-b border-white/5 hover:bg-white/5"
                                            >
                                                <td className="py-4 font-medium">
                                                    <Link
                                                        to={`/admin/yacht-details/${yacht.id}`}
                                                        className="text-blue-400 hover:text-blue-300 transition-colors cursor-pointer"
                                                    >
                                                        {yacht.name}
                                                    </Link>
                                                </td>
                                                <td className="py-4">
                                                    {yacht.captain}
                                                </td>
                                                <td className="py-4">
                                                    {getYachtTypeText(
                                                        yacht.type
                                                    )}
                                                </td>
                                                <td className="py-4">
                                                    {yacht.capacity} kişi
                                                </td>
                                                <td className="py-4">
                                                    {yacht.shipBoy ||
                                                        "Bilinmiyor"}
                                                </td>
                                                <td className="py-4">
                                                    {yacht.model} / {yacht.year}
                                                </td>
                                                <td className="py-4">
                                                    {yacht.length}m
                                                </td>
                                                <td className="py-4">
                                                    <span className="px-2 py-1 rounded-full text-xs bg-gray-500/20 text-gray-400">
                                                        Bilinmiyor
                                                    </span>
                                                </td>
                                                <td className="py-4 text-center">
                                                    <div className="flex justify-center gap-2">
                                                        <Link
                                                            to={`/admin/yacht-details/${yacht.id}`}
                                                            className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                                            title="Detayları Gör"
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                                />
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                                />
                                                            </svg>
                                                        </Link>
                                                        <button
                                                            onClick={() =>
                                                                showDeleteConfirmation(
                                                                    yacht
                                                                )
                                                            }
                                                            className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                                            title="Sil"
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredYachts.length === 0 && searchTerm && (
                                            <tr>
                                                <td colSpan="9" className="py-8 text-center">
                                                    <div className="flex flex-col items-center justify-center text-white/60">
                                                        <svg
                                                            className="w-12 h-12 mb-4 text-white/30"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                            />
                                                        </svg>
                                                        <p className="text-lg mb-2">Arama sonucu bulunamadı</p>
                                                        <p className="text-sm">
                                                            "{searchTerm}" için hiç yat bulunamadı.
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Yat Ekleme Modal */}
                    <AddYachtModal
                        show={showAddForm}
                        onClose={handleCloseModal}
                        newYacht={newYacht}
                        handleInputChange={handleInputChange}
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        formErrors={formErrors}
                        dragActive={dragActive}
                        handleDrag={handleDrag}
                        handleDrop={handleDrop}
                        handleFileChange={handleFileChange}
                        imageUploading={imageUploading}
                        removeImage={removeImage}
                        imageDescriptions={imageDescriptions}
                        handleImageDescriptionChange={handleImageDescriptionChange}
                    />

                    {/* Silme Onay Dialog'u */}
                    {deleteConfirm.show && createPortal(
                        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]">
                            <div className="bg-gray-800/95 backdrop-blur-md rounded-2xl w-full max-w-md p-8 border border-white/10 shadow-2xl shadow-black/50">
                                <div className="text-center">
                                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-500/20 mb-4">
                                        <svg
                                            className="h-8 w-8 text-red-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                            />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-3">
                                        Yatı Sil
                                    </h3>
                                    <p className="text-white/80 mb-6">
                                        <strong className="text-blue-400">
                                            {deleteConfirm.yacht?.name}
                                        </strong>{" "}
                                        adlı yatı silmek istediğinizden emin
                                        misiniz? Bu işlem geri alınamaz.
                                    </p>
                                    <div className="flex justify-center gap-3">
                                        <button
                                            onClick={hideDeleteConfirmation}
                                            disabled={isDeleting}
                                            className="px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/10 transition-all duration-300 disabled:opacity-50 font-medium"
                                        >
                                            İptal
                                        </button>
                                        <button
                                            onClick={() =>
                                                deleteYacht(
                                                    deleteConfirm.yacht?.id
                                                )
                                            }
                                            disabled={isDeleting}
                                            className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-300 disabled:from-red-400 disabled:to-red-500 flex items-center gap-2 font-medium shadow-lg shadow-red-500/30"
                                        >
                                            {isDeleting ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                                    Siliniyor...
                                                </>
                                            ) : (
                                                <>
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                        />
                                                    </svg>
                                                    Sil
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}

                    {/* Bildirim */}
                    {notification.show && (
                        <div
                            className={`fixed top-4 right-4 p-4 rounded-xl shadow-lg ${
                                notification.type === "success"
                                    ? "bg-green-500"
                                    : notification.type === "warning"
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                            } text-white z-50`}
                        >
                            {notification.message}
                        </div>
                    )}

                    {/* Notification Modal */}
                    <NotificationModal
                        show={showNotificationModal.show}
                        type={showNotificationModal.type}
                        title={showNotificationModal.title}
                        message={showNotificationModal.message}
                        onClose={hideNotificationModal}
                        autoClose={true}
                        duration={1000}
                    />
        </AdminLayout>
    );
}

export default YachtManagement;
